import { BusMessage, calcArrayByteLength, MessageFactory, calcStringByteLength, UID_BYTES_PER_ELEMENT, calcTypeByteLength, BOOLEAN_BYTES_PER_ELEMENT } from "flavr-common";
/**
 * Broadcasts the authenticated user's profile data so client modules can initialize session-specific UI and avatar state.
 */
export class UserAuthenticatedMessage extends BusMessage {
    get byteLength() {
        return (calcStringByteLength(this.firstname) +
            calcStringByteLength(this.lastname) +
            calcArrayByteLength(this.roles) +
            calcStringByteLength(this.avatarimageUrl) +
            Uint32Array.BYTES_PER_ELEMENT +
            Uint32Array.BYTES_PER_ELEMENT);
    }
    constructor(firstname, lastname, roles, avatarimageUrl, avatarbaseColor) {
        super();
        this.firstname = firstname;
        this.lastname = lastname;
        this.roles = roles;
        this.avatarimageUrl = avatarimageUrl;
        this.avatarbaseColor = avatarbaseColor;
    }
    serialization(stream) {
        this.firstname = stream.utf16String(this.firstname);
        this.lastname = stream.utf16String(this.lastname);
        this.roles = stream.array(this.roles);
        this.avatarimageUrl = stream.utf16String(this.avatarimageUrl);
        this.avatarbaseColor = stream.uint32(this.avatarbaseColor);
    }
    static is(msg) {
        return msg.type === UserAuthenticatedMessage.typestatic;
    }
}
UserAuthenticatedMessage.typestatic = "UserAuthenticatedMessage";
UserAuthenticatedMessage.subjectstatic = "session";
MessageFactory.register(UserAuthenticatedMessage);
/**
 * Starts a session in the specified initial room so the client can build the first room hierarchy and load its content.
 */
export class StartSessionMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.initialRoomId);
    }
    constructor(initialRoomId) {
        super();
        this.initialRoomId = initialRoomId;
    }
    serialization(stream) {
        this.initialRoomId = stream.utf16String(this.initialRoomId);
    }
    static is(msg) {
        return msg.type === StartSessionMessage.typestatic;
    }
}
StartSessionMessage.typestatic = "StartSessionMessage";
StartSessionMessage.subjectstatic = "session";
MessageFactory.register(StartSessionMessage);
/**
 * Ends the current session so modules can clear session-specific state and unload active content.
 */
export class EndSessionMessage extends BusMessage {
    serialization() { }
    static is(msg) {
        return msg.type === EndSessionMessage.typestatic;
    }
}
EndSessionMessage.typestatic = "EndSessionMessage";
EndSessionMessage.subjectstatic = "session";
MessageFactory.register(EndSessionMessage);
/**
 * Announces that a client connected to the current session.
 */
export class SessionConnectMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.clientId);
    }
    constructor(clientId) {
        super();
        this.clientId = clientId;
    }
    serialization(stream) {
        this.clientId = stream.utf16String(this.clientId);
    }
    static is(msg) {
        return msg.type === SessionConnectMessage.typestatic;
    }
}
SessionConnectMessage.typestatic = "SessionConnectMessage";
SessionConnectMessage.subjectstatic = "session";
MessageFactory.register(SessionConnectMessage);
/**
 * Announces that a client disconnected from the current session.
 */
export class SessionDisconnectMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.clientId) + Uint32Array.BYTES_PER_ELEMENT; // clientIdx
    }
    constructor(clientId, clientIdx) {
        super();
        this.clientId = clientId;
        this.clientIdx = clientIdx;
    }
    serialization(stream) {
        this.clientId = stream.utf16String(this.clientId);
        this.clientIdx = stream.uint32(this.clientIdx);
    }
    static is(msg) {
        return msg.type === SessionDisconnectMessage.typestatic;
    }
}
SessionDisconnectMessage.typestatic = "SessionDisconnectMessage";
SessionDisconnectMessage.subjectstatic = "session";
MessageFactory.register(SessionDisconnectMessage);
/**
 * Requests creation of the runtime avatar model for a session member in the given room.
 */
export class SessionCreateAvatarObject extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.clientId) + UID_BYTES_PER_ELEMENT * 2; // uid, roomUid
    }
    constructor(clientId, uid, roomUid) {
        super();
        this.clientId = clientId;
        this.uid = uid;
        this.roomUid = roomUid;
    }
    serialization(stream) {
        this.clientId = stream.utf16String(this.clientId);
        this.uid = stream.uid(this.uid);
        this.roomUid = stream.uid(this.roomUid);
    }
    static is(msg) {
        return msg.type === SessionCreateAvatarObject.typestatic;
    }
}
SessionCreateAvatarObject.typestatic = "SessionCreateAvatarObject";
SessionCreateAvatarObject.subjectstatic = "session";
MessageFactory.register(SessionCreateAvatarObject);
/**
 * Requests removal of the runtime avatar model for a session member.
 */
export class SessionRemoveAvatarObject extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.clientId) + UID_BYTES_PER_ELEMENT;
    }
    constructor(clientId, uid) {
        super();
        this.clientId = clientId;
        this.uid = uid;
    }
    serialization(stream) {
        this.clientId = stream.utf16String(this.clientId);
        this.uid = stream.uid(this.uid);
    }
    static is(msg) {
        return msg.type === SessionRemoveAvatarObject.typestatic;
    }
}
SessionRemoveAvatarObject.typestatic = "SessionRemoveAvatarObject";
SessionRemoveAvatarObject.subjectstatic = "session";
MessageFactory.register(SessionRemoveAvatarObject);
/**
 * Updates the local user's avatar or custom username so the rest of the session can reflect the new identity data.
 */
export class ChangeLocalUserInfoMessage extends BusMessage {
    get byteLength() {
        return calcTypeByteLength(this.params);
    }
    constructor(params) {
        super();
        this.params = params;
    }
    serialization(stream) {
        if (stream.isReading) {
            this.params = {};
        }
        this.params = stream.type(this.params);
    }
    static is(msg) {
        return msg.type === ChangeLocalUserInfoMessage.typestatic;
    }
}
ChangeLocalUserInfoMessage.typestatic = "ChangeLocalUserInfoMessage";
ChangeLocalUserInfoMessage.subjectstatic = "session";
MessageFactory.register(ChangeLocalUserInfoMessage);
/**
 * Triggers the local user spawn flow.
 */
export class UserSpawnMessage extends BusMessage {
    get byteLength() {
        return 0;
    }
    serialization() { }
    static is(msg) {
        return msg.type === UserSpawnMessage.typestatic;
    }
}
UserSpawnMessage.typestatic = "UserSpawnMessage";
UserSpawnMessage.subjectstatic = "general";
MessageFactory.register(UserSpawnMessage);
/**
 * Announces that the local user finished spawning in the specified room.
 */
export class UserSpawnedMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT;
    }
    constructor(roomUid) {
        super();
        this.roomUid = roomUid;
    }
    serialization(stream) {
        this.roomUid = stream.uid(this.roomUid);
    }
    static is(msg) {
        return msg.type === UserSpawnedMessage.typestatic;
    }
}
UserSpawnedMessage.typestatic = "UserSpawnedMessage";
UserSpawnedMessage.subjectstatic = "general";
MessageFactory.register(UserSpawnedMessage);
/**
 * Requests the available spawn points for a room, optionally filtered by flags or identifiers.
 */
export class SpawnPointsInRoomRequestMessage extends BusMessage {
    get byteLength() {
        return (UID_BYTES_PER_ELEMENT +
            Uint32Array.BYTES_PER_ELEMENT + // flags
            calcStringByteLength(this.filter.id) + // id
            BOOLEAN_BYTES_PER_ELEMENT + // random
            BOOLEAN_BYTES_PER_ELEMENT); // waitForScriptsCreationFinished
    }
    constructor(roomUid, filter = {}) {
        super();
        this.roomUid = roomUid;
        this.filter = filter;
    }
    serialization(stream) {
        if (stream.isReading) {
            this.filter = {};
        }
        this.roomUid = stream.uid(this.roomUid);
        this.filter.flags = stream.uint32Safe(this.filter.flags);
        this.filter.id = stream.utf16StringSafe(this.filter.id);
        this.filter.random = stream.booleanSafe(this.filter.random);
        this.filter.waitForScriptsCreationFinished = stream.booleanSafe(this.filter.waitForScriptsCreationFinished);
    }
    static is(msg) {
        return msg.type === SpawnPointsInRoomRequestMessage.typestatic;
    }
}
SpawnPointsInRoomRequestMessage.typestatic = "SpawnPointsInRoomRequestMessage";
SpawnPointsInRoomRequestMessage.subjectstatic = "general";
MessageFactory.register(SpawnPointsInRoomRequestMessage);
/**
 * Returns the spawn points that are available in the requested room.
 */
export class SpawnPointsInRoomResponseMessage extends BusMessage {
    get byteLength() {
        let byteLength = Uint32Array.BYTES_PER_ELEMENT; // pointsLength
        for (let i = 0; i < this.points.length; ++i) {
            byteLength +=
                UID_BYTES_PER_ELEMENT +
                    Uint32Array.BYTES_PER_ELEMENT + // flags
                    Float32Array.BYTES_PER_ELEMENT * (3 + 4 + 3); // position, orientation, scale
        }
        return byteLength;
    }
    constructor(points) {
        super();
        this.points = points;
    }
    serialization(stream) {
        if (stream.isReading) {
            this.points = [];
        }
        const pointsLength = stream.uint32(this.points.length);
        for (let i = 0; i < pointsLength; i++) {
            let point = this.points[i];
            if (!point) {
                this.points[i] = point = {};
            }
            point.uid = stream.uid(point.uid);
            point.flags = stream.uint32(point.flags);
            point.position = stream.number3(point.position);
            point.orientation = stream.number4(point.orientation);
            point.scale = stream.number3(point.scale);
        }
    }
    static is(msg) {
        return msg.type === SpawnPointsInRoomResponseMessage.typestatic;
    }
}
SpawnPointsInRoomResponseMessage.typestatic = "SpawnPointsInRoomResponseMessage";
SpawnPointsInRoomResponseMessage.subjectstatic = "general";
MessageFactory.register(SpawnPointsInRoomResponseMessage);
/**
 * Publishes the current session permission set to interested client modules.
 */
export class SessionPermissionsMessage extends BusMessage {
    get byteLength() {
        return calcArrayByteLength(this.permissions);
    }
    constructor(permissions) {
        super();
        this.permissions = permissions;
    }
    serialization(stream) {
        this.permissions = stream.array(this.permissions);
    }
    static is(msg) {
        return msg.type === SessionPermissionsMessage.typestatic;
    }
}
SessionPermissionsMessage.typestatic = "SessionPermissionsMessage";
SessionPermissionsMessage.subjectstatic = "session";
MessageFactory.register(SessionPermissionsMessage);
/**
 * Reports that the server connection was closed together with the close code.
 */
export class ServerConnectionClosedMessage extends BusMessage {
    get byteLength() {
        return Uint32Array.BYTES_PER_ELEMENT;
    }
    constructor(code) {
        super();
        this.code = code;
    }
    serialization(stream) {
        this.code = stream.uint32(this.code);
    }
    static is(msg) {
        return msg.type === ServerConnectionClosedMessage.typestatic;
    }
}
ServerConnectionClosedMessage.typestatic = "ServerConnectionClosedMessage";
ServerConnectionClosedMessage.subjectstatic = "session";
MessageFactory.register(ServerConnectionClosedMessage);
/**
 * Signals that peer-specific current-room synchronization finished.
 */
export class PeerCurrentFineshedMessage extends BusMessage {
    get byteLength() {
        return 0;
    }
    constructor(roomId) {
        super();
        this.roomId = roomId;
    }
    serialization() { }
    static is(msg) {
        return msg.type === PeerCurrentFineshedMessage.typestatic;
    }
}
PeerCurrentFineshedMessage.typestatic = "PeerCurrentFineshedMessage";
PeerCurrentFineshedMessage.subjectstatic = "local";
MessageFactory.register(PeerCurrentFineshedMessage);
/**
 * Shares the current average offset between client time and server time.
 */
export class ServerTimeMessage extends BusMessage {
    get byteLength() {
        return Uint32Array.BYTES_PER_ELEMENT; // avgServerTimeOffset
    }
    constructor(avgServerTimeOffset) {
        super();
        this.avgServerTimeOffset = avgServerTimeOffset;
    }
    serialization(stream) {
        this.avgServerTimeOffset = stream.uint32(this.avgServerTimeOffset);
    }
    static is(msg) {
        return msg.type === ServerTimeMessage.typestatic;
    }
}
ServerTimeMessage.typestatic = "ServerTimeMessage";
ServerTimeMessage.subjectstatic = "general";
MessageFactory.register(ServerTimeMessage);
